import { html, css, nothing } from 'lit';
import type { PropertyValues } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { msg, localized } from '@lit/localize';
import { SsoElement } from '../components/sso-base.js';
import { endpoints } from '../api/client.js';
import { navigate } from '../router.js';
import { TargetApp } from '../api/types.js';
import type { AppKey, LauncherApp } from '../api/types.js';
import { appDisplayName } from '../app-names.js';
import '../components/sso-app-tile.js';
import '../components/sso-alert.js';
import '../components/sso-spinner.js';

/**
 * `<launcher-page>` — portal shown on direct SSO access or after sign-in (SSO-MOCKUPS.md §8).
 *
 * Renders up to 3 app tiles filtered server-side by eligibility (D5). Clicking "Open" opens `/go/{app}`
 * in a new tab, which mints a single-use ticket and 302s to the app with `?SSOCode=`; the handoff is a
 * browser navigation, not a fetch, and the SPA never builds ticket URLs.
 */
@customElement('launcher-page')
@localized()
export class LauncherPage extends SsoElement {
  static override styles = [
    SsoElement.styles,
    css`
      .wrap {
        max-width: 960px;
        margin: 0 auto;
        text-align: center;
      }
      h1 {
        margin-top: var(--sso-space-lg);
      }
      .tiles {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
        gap: var(--sso-space);
        margin: var(--sso-space-lg) 0;
        text-align: start;
      }
      .footer {
        display: flex;
        gap: var(--sso-space);
        justify-content: center;
        flex-wrap: wrap;
        margin-top: var(--sso-space-lg);
        font-size: 0.9rem;
      }
      .empty {
        color: var(--sso-color-muted);
      }
    `,
  ];

  @state() private apps?: LauncherApp[];
  @state() private error = '';

  override async connectedCallback(): Promise<void> {
    super.connectedCallback();
    try {
      const result = await endpoints.launcher();
      this.apps = result.apps;
    } catch {
      this.error = msg('Could not load your applications. Please try again.');
    }
  }

  override updated(changed: PropertyValues): void {
    super.updated(changed);
    // App tiles load asynchronously — focus the first tile's Open button once they have rendered.
    this.focusFirstControl();
  }

  /** Map the numeric TargetApp enum to its canonical key. */
  private keyFor(app: TargetApp): AppKey {
    switch (app) {
      case TargetApp.Crm:
        return 'crm';
      case TargetApp.Export:
        return 'export';
      case TargetApp.Callimachus:
        return 'callimachus';
      default:
        return 'crm';
    }
  }

  /**
   * Open an app in a new tab. The handoff is a real browser navigation (NOT a fetch): `/go/{app}` 302s
   * to the app with `?SSOCode=`. The SPA only navigates — it never builds ticket URLs. `noopener` keeps
   * the launched app from holding a reference back to this tab. Runs synchronously inside the click
   * gesture so it isn't treated as a blocked pop-up.
   */
  private open(e: CustomEvent<string>): void {
    const app = e.detail;
    window.open(`/go/${encodeURIComponent(app)}`, '_blank', 'noopener');
  }

  override render() {
    if (!this.apps && !this.error) {
      return html`<sso-spinner aria-label=${msg('Loading')}></sso-spinner>`;
    }
    return html`
      <div class="wrap">
        <h1>${msg('Welcome')}</h1>
        <p>${msg('Choose an application to open')}</p>
        ${this.error ? html`<sso-alert kind="error">${this.error}</sso-alert>` : nothing}
        ${this.apps && this.apps.length
          ? html`<div class="tiles" @open-app=${(e: CustomEvent<string>) => this.open(e)}>
              ${this.apps.map(
                (a) =>
                  html`<sso-app-tile
                    app=${this.keyFor(a.app)}
                    label=${appDisplayName(a.app) || a.displayName}></sso-app-tile>`,
              )}
            </div>`
          : this.apps
            ? html`<p class="empty">${msg('No active subscription found.')}</p>`
            : nothing}
        <div class="footer">
          <a href="/security" @click=${(e: Event) => {
            e.preventDefault();
            navigate('/security');
          }}>${msg('Security')}</a>
          <a href="/logout">${msg('Sign out')}</a>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'launcher-page': LauncherPage;
  }
}
